<template>
  <modal
    elem-id="modal_payment_link"
    size="compactDesktop"
    :title="modalTitle"
    :close-label="t('Fechar')"
    :save-label="''"
    :show="isVisible"
    @close="handleClose"
    @dismiss="handleClose"
  >
    <!-- Content only renders when modal is visible (prevents API calls on mount) -->
    <template v-if="isVisible">
      <!-- Loading state -->
      <div v-if="isLoading" class="loading-container">
        <i class="pi pi-spin pi-spinner" style="font-size: 2rem;"></i>
        <p>{{ t('Carregando...') }}</p>
      </div>

      <!-- View mode: Show payment link details -->
      <div v-else-if="paymentLink">
        <!-- Status badge -->
        <div class="status-section">
          <PaymentLinksStatusBadge
            :status="paymentLink.status"
            :payment-link="paymentLink"
            :show-details="true"
          />
        </div>

        <!-- Payment link URL -->
        <box
          v-if="paymentLink.url && paymentLink.status === 'active'"
          type="primary"
          :title="t('Link de pagamento')"
          elem-id="boxUrlPaymentLink">

          <div class="url-container">
            <input
              data-testid="payment-links-url-input"
              type="text"
              class="url-input"
              :value="paymentLink.url"
              readonly
              ref="urlInputRef"
            />
            <Button
              data-testid="payment-links-copy-url-button"
              icon="pi pi-copy"
              class="p-button-outlined"
              v-tooltip.top="t('Copiar link')"
              @click="handleCopyUrl"
            />
          </div>

          <div class="action-buttons">
            <Button
              data-testid="payment-links-send-whatsapp-button"
              :label="t('Enviar via WhatsApp')"
              icon="pi pi-whatsapp"
              severity="success"
              class="whatsapp-button"
              @click="handleSendWhatsApp"
            />
          </div>
        </box>

        <!-- Payment link details -->
        <box
          type="primary"
          :title="t('Detalhes')"
          elem-id="boxDetailsPaymentLink">

          <CompactDataTable :value="detailsData as any" />
        </box>

        <!-- Recebimentos (if any) -->
        <box
          v-if="paymentLink.recebimentos && paymentLink.recebimentos.length > 0"
          type="primary"
          :title="t('Recebimentos gerados')"
          elem-id="boxRecebimentosPaymentLink">

          <div class="recebimentos-list">
            <div
              v-for="rec in paymentLink.recebimentos"
              :key="rec.recebimento_id"
              class="recebimento-item"
            >
              <div class="recebimento-info">
                <span class="recebimento-value">{{ formatCurrency((rec.recebimento_valor ?? 0) / 100) }}</span>
                <span class="recebimento-date">{{ formatDate(rec.recebimento_data) }}</span>
                <span v-if="rec.installment_number" class="recebimento-installment">
                  {{ t('Parcela') }} {{ rec.installment_number }}
                </span>
              </div>
            </div>
          </div>
        </box>

        <!-- Timeline -->
        <box
          type="primary"
          :title="t('Histórico')"
          elem-id="boxHistoryPaymentLink">

          <PaymentLinksTimeline
            :events="timelineEvents"
            :is-loading="isLoadingTimeline"
          />
        </box>

        <!-- Action buttons -->
        <div class="actions-section">
          <!-- Sync button - shows when link has provider_link_id -->
          <Button
            v-if="canSync"
            data-testid="payment-links-modal-sync-button"
            :label="t('Sincronizar')"
            icon="pi pi-sync"
            severity="secondary"
            outlined
            class="action-button"
            :loading="isSyncing"
            :disabled="isSyncing"
            v-tooltip.top="t('Buscar status atualizado do provedor de pagamento')"
            @click="handleSync"
          />

          <!-- Cancel button -->
          <Button
            v-if="canCancel"
            data-testid="payment-links-modal-cancel-button"
            :label="t('Cancelar Link')"
            icon="pi pi-times"
            severity="danger"
            outlined
            class="action-button"
            @click="handleCancel"
          />
        </div>
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import { usePaymentLinks } from '../../composables/usePaymentLinks';
import type { SyncResult, SyncSummary } from '../../composables/usePaymentLinks';
import { useDateFormatter } from '../../composables/useDateFormatter';
import { useConfirmAction } from '../../composables/useConfirmAction';
import PaymentLinksStatusBadge from './PaymentLinksStatusBadge.vue';
import PaymentLinksTimeline from './PaymentLinksTimeline.vue';
import CompactDataTable from '../../grid/CompactDataTable.vue';

type Id = number | string;

interface Recebimento {
  recebimento_id?: Id;
  recebimento_valor?: number;
  recebimento_data?: string;
  installment_number?: number | string;
  [key: string]: unknown;
}

interface PaymentLink {
  id?: Id;
  status?: string;
  amount?: number;
  url?: string;
  provider_link_id?: string;
  payment_method?: string;
  charge_type?: string;
  installments?: number;
  due_date?: string | null;
  description?: string;
  created_at?: string;
  paid_at?: string;
  paid_amount?: number;
  recebimentos?: Recebimento[];
  [key: string]: unknown;
}

interface TimelineEvent {
  event_type?: string;
  event_description?: string;
  created_at?: string;
  [key: string]: unknown;
}

interface DetailsRow {
  prop: string;
  value: string | number | undefined;
}

interface ModalOpenPayload {
  id?: Id;
  owner?: string;
  ownerId?: Id;
  customerPhone?: string;
  customerName?: string;
}

const { t, n } = useI18n();
const {
  getById,
  getHistory,
  deactivateLink,
  syncPaymentLink,
  getLinkStatusConfig,
  canCancel: checkCanCancel
} = usePaymentLinks();
const { confirmAction } = useConfirmAction();
const { formatDate: formatDateLocale, formatDateTime: formatDateTimeLocale } = useDateFormatter();

// State
const isVisible = ref<boolean>(false);
const isLoading = ref<boolean>(false);
const isLoadingTimeline = ref<boolean>(false);
const isSyncing = ref<boolean>(false);
const mode = ref<'view' | 'create'>('view');
const customerPhone = ref<string>('');
const customerName = ref<string>('');
const paymentLink = ref<PaymentLink | null>(null);
const timelineEvents = ref<TimelineEvent[]>([]);
const urlInputRef = ref<HTMLInputElement | null>(null);

// Computed
const modalTitle = computed<string>(() => {
  return t('Link de pagamento');
});

const canCancel = computed<boolean>(() => {
  return !!paymentLink.value && checkCanCancel(paymentLink.value);
});

const canSync = computed<boolean>(() => {
  // Can sync if:
  // 1. Has a payment link loaded
  // 2. Has a provider_link_id (linked to payment provider)
  // 3. Status is not cancelled or refunded (terminal states)
  if (!paymentLink.value) return false;
  if (!paymentLink.value.provider_link_id) return false;

  const terminalStatuses = ['cancelled', 'refunded'];
  return !terminalStatuses.includes(paymentLink.value.status || '');
});

const detailsData = computed<DetailsRow[]>(() => {
  if (!paymentLink.value) return [];

  const link = paymentLink.value;
  const data: DetailsRow[] = [
    { prop: 'ID', value: link.id as string | number | undefined },
    { prop: t('Valor'), value: formatCurrency((link.amount ?? 0) / 100) },
    { prop: t('Status'), value: getLinkStatusConfig(link.status).label },
    { prop: t('Forma'), value: getPaymentMethodLabel(link.payment_method) },
    { prop: t('Tipo'), value: link.charge_type === 'single' ? t('Pagamento unico') : t('Parcelado') }
  ];

  if ((link.installments ?? 0) > 1) {
    const installments = link.installments ?? 1;
    data.push({ prop: t('Parcelas'), value: `${installments}x de ${formatCurrency((link.amount ?? 0) / installments / 100)}` });
  }

  if (link.due_date) {
    data.push({ prop: t('Vencimento'), value: formatDate(link.due_date) });
  }

  if (link.description) {
    data.push({ prop: t('Descricao'), value: link.description });
  }

  data.push({ prop: t('Criado em'), value: formatDateTime(link.created_at) });

  if (link.paid_at) {
    data.push({ prop: t('Pago em'), value: formatDateTime(link.paid_at) });
  }

  if (link.paid_amount) {
    data.push({ prop: t('Valor pago'), value: formatCurrency(link.paid_amount / 100) });
  }

  return data;
});

// Methods
const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value || 0);
};

const formatDate = (dateStr?: string | null): string => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return formatDateLocale(date);
};

const formatDateTime = (dateStr?: string | null): string => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return formatDateTimeLocale(date);
};

const getPaymentMethodLabel = (method?: string): string => {
  const labels: Record<string, string> = {
    any: t('Qualquer forma'),
    pix: 'PIX',
    boleto: 'Boleto',
    credit_card: t('Cartão de Crédito')
  };
  return labels[method || ''] || method || '';
};

const loadPaymentLink = async (id: Id): Promise<void> => {
  log('PaymentLinksModal', 'loadPaymentLink', 'Loading payment link', id);
  isLoading.value = true;

  try {
    paymentLink.value = (await getById(Number(id)) as unknown as PaymentLink) ?? null;
    log('PaymentLinksModal', 'loadPaymentLink', 'Loaded', paymentLink.value);

    // Load timeline
    loadTimeline(id);
  } catch (error) {
    log('PaymentLinksModal', 'loadPaymentLink', 'Error', error);
    eventBus.emit('globalMessage.error', t('Erro ao carregar link de pagamento'));
  } finally {
    isLoading.value = false;
  }
};

const loadTimeline = async (id: Id): Promise<void> => {
  log('PaymentLinksModal', 'loadTimeline', 'Loading timeline', id);
  isLoadingTimeline.value = true;

  try {
    timelineEvents.value = (await getHistory(Number(id))) as unknown as TimelineEvent[];
    log('PaymentLinksModal', 'loadTimeline', 'Loaded events', timelineEvents.value.length);
  } catch (error) {
    log('PaymentLinksModal', 'loadTimeline', 'Error', error);
  } finally {
    isLoadingTimeline.value = false;
  }
};

const handleCopyUrl = async (): Promise<void> => {
  if (!paymentLink.value?.url) return;

  try {
    await navigator.clipboard.writeText(paymentLink.value.url);
    eventBus.emit('globalMessage.success', t('Link copiado!'));
  } catch (error) {
    log('PaymentLinksModal', 'handleCopyUrl', 'Error', error);
    // Fallback: select the input
    urlInputRef.value?.select();
  }
};

const handleSendWhatsApp = (): void => {
  if (!paymentLink.value?.url) return;

  log('PaymentLinksModal', 'handleSendWhatsApp', 'Opening WhatsApp', { customerPhone: customerPhone.value, customerName: customerName.value });

  const phone = customerPhone.value?.replace(/\D/g, '') || '';
  const name = customerName.value || t('cliente');
  const amount = formatCurrency((paymentLink.value.amount ?? 0) / 100);
  const url = paymentLink.value.url;

  // Build message with proper formatting
  const message = `Olá ${name}! Segue o link para pagamento de ${amount}: ${url}`;

  const whatsappUrl = `https://api.whatsapp.com/send?phone=${phone}&text=${encodeURIComponent(message)}`;
  window.open(whatsappUrl, '_blank');
};

const handleCancel = (): void => {
  if (!paymentLink.value?.id) return;

  confirmAction({
    message: t('Tem certeza que deseja cancelar este link de pagamento?'),
    header: t('Confirmação'),
    onAccept: async () => {
      log('PaymentLinksModal', 'handleCancel', 'Cancelling link', paymentLink.value!.id);
      isLoading.value = true;

      try {
        await deactivateLink(Number(paymentLink.value!.id));
        eventBus.emit('globalMessage.success', t('Link de pagamento cancelado'));
        eventBus.emit('payment-link.cancelled', { paymentLinkId: paymentLink.value!.id });

        // Reload the link to show updated status
        await loadPaymentLink(paymentLink.value!.id!);
      } catch (error) {
        log('PaymentLinksModal', 'handleCancel', 'Error', error);
        eventBus.emit('globalMessage.error', t('Erro ao cancelar link de pagamento'));
      } finally {
        isLoading.value = false;
      }
    }
  });
};

const handleSync = async (): Promise<void> => {
  if (!paymentLink.value?.id) return;

  log('PaymentLinksModal', 'handleSync', 'Syncing payment link', paymentLink.value.id);
  isSyncing.value = true;

  try {
    const result: SyncResult = await syncPaymentLink(Number(paymentLink.value.id));
    log('PaymentLinksModal', 'handleSync', 'Sync result', result);

    // Build message based on sync results
    const summary = result.summary || {};
    const chargesFound = summary.charges_found || 0;
    const chargesOk = summary.charges_processed_ok || 0;
    const chargesWithErrors = summary.charges_with_errors || 0;
    const totalPaid = summary.total_paid || 0;

    // Build detailed sync result message
    let message = '';
    let severity: 'success' | 'warning' | 'error' = 'success';

    if (chargesFound === 0) {
      message = t('Nenhuma cobrança encontrada para este link');
      severity = 'warning';
    } else if (chargesWithErrors > 0 && chargesOk === 0) {
      // All charges failed
      message = t('Erro ao sincronizar') + `: ${chargesWithErrors} ${t('cobranças com erro')}`;
      severity = 'error';
    } else if (chargesWithErrors > 0) {
      // Partial success
      message = t('Sincronização parcial') + `: ${chargesOk}/${chargesFound} ${t('cobranças processadas')}`;
      if (totalPaid > 0) {
        message += ` - ${t('Total pago')}: ${n(totalPaid, 'currency')}`;
      }
      severity = 'warning';
    } else {
      // Full success
      message = t('Sincronização concluída') + `: ${chargesOk} ${t('cobranças processadas')}`;
      if (totalPaid > 0) {
        message += ` - ${t('Total pago')}: ${n(totalPaid, 'currency')}`;
      }
      if (result.status_changed) {
        message += ` - ${t('Status')}: ${result.current_status}`;
      }
      severity = 'success';
    }

    // Emit globalMessage with appropriate severity
    if (severity === 'success') {
      eventBus.emit('globalMessage.success', message);
    } else if (severity === 'warning') {
      eventBus.emit('globalMessage.warning', message);
    } else {
      eventBus.emit('globalMessage.error', message);
    }

    // Emit event for other components to react
    if (result.status_changed || chargesOk > 0) {
      eventBus.emit('payment-link.synced', {
        paymentLinkId: paymentLink.value.id,
        previousStatus: result.previous_status,
        newStatus: result.current_status,
        summary: summary
      });
    }

    // Reload the link to show updated data and timeline
    await loadPaymentLink(paymentLink.value.id);
  } catch (error) {
    const errMsg = error instanceof Error ? error.message : String(error);
    log('PaymentLinksModal', 'handleSync', 'Error', error);
    eventBus.emit('globalMessage.error', errMsg || t('Erro ao sincronizar link de pagamento'));
  } finally {
    isSyncing.value = false;
  }
};

const handleClose = (): void => {
  log('PaymentLinksModal', 'handleClose', 'Closing modal');
  isVisible.value = false;
  paymentLink.value = null;
  timelineEvents.value = [];
};

// Event handlers
const handleModalOpen = (data?: ModalOpenPayload): void => {
  log('PaymentLinksModal', 'handleModalOpen', 'Received open event', data);

  // Store customer info if provided (for WhatsApp)
  customerPhone.value = data?.customerPhone || '';
  customerName.value = data?.customerName || '';

  if (data?.id) {
    // View existing payment link
    mode.value = 'view';
    loadPaymentLink(data.id);
    isVisible.value = true;
  } else if (data?.owner && data?.ownerId) {
    // Create new payment link - redirect to wizard
    log('PaymentLinksModal', 'handleModalOpen', 'Redirecting to wizard for create');
    eventBus.emit('modal.payment-link-wizard.open', data);
    // Don't open this modal for create mode
    return;
  }
};

// Lifecycle
onMounted(() => {
  log('PaymentLinksModal', 'onMounted', 'Component mounted');

  // Standard modal events
  eventBus.on('modal.payment-link.open', handleModalOpen);
  eventBus.on('modal.payment-link.close', handleClose);

  // Legacy events (for backward compatibility)
  eventBus.on('payment-link.open', handleModalOpen);
  eventBus.on('payment-link.create', handleModalOpen);
});

onBeforeUnmount(() => {
  log('PaymentLinksModal', 'onBeforeUnmount', 'Cleaning up event listeners');

  eventBus.off('modal.payment-link.open', handleModalOpen);
  eventBus.off('modal.payment-link.close', handleClose);
  eventBus.off('payment-link.open', handleModalOpen);
  eventBus.off('payment-link.create', handleModalOpen);
});

// Expose methods
defineExpose({
  open: handleModalOpen,
  close: handleClose
});
</script>

<style scoped>
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  gap: 1rem;
  color: var(--text-color-secondary);
}

.status-section {
  display: flex;
  justify-content: center;
  margin-bottom: 1.5rem;
}

.url-container {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.url-input {
  flex: 1;
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--surface-border);
  border-radius: 4px;
  background-color: var(--surface-ground);
  font-size: 0.875rem;
  color: var(--text-color);
}

.action-buttons {
  display: flex;
  gap: 0.5rem;
}

.whatsapp-button {
  width: 100%;
}

.recebimentos-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.recebimento-item {
  padding: 0.75rem;
  background-color: var(--surface-ground);
  border-radius: 4px;
  border: 1px solid var(--surface-border);
}

.recebimento-info {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.recebimento-value {
  font-weight: 600;
  color: var(--green-600);
}

.recebimento-date {
  color: var(--text-color-secondary);
  font-size: 0.875rem;
}

.recebimento-installment {
  background-color: var(--primary-100);
  color: var(--primary-700);
  padding: 0.125rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
}

.actions-section {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--surface-border);
}

.action-button {
  width: 100%;
}
</style>
